<template>
  <div class="pipeline-timeline">
    <div
      v-for="(entry, index) in timeline"
      :key="entry.stage"
      class="pipeline-stage">
      <div class="pipeline-stage__rail">
        <div
          class="pipeline-stage__dot"
          :class="`pipeline-stage__dot--${entry.color}`">
          <q-icon
            :name="entry.icon"
            size="13px" />
        </div>
        <div
          v-if="index < timeline.length - 1"
          class="pipeline-stage__line"
          :class="{ 'pipeline-stage__line--done': entry.status === 'completed' }" />
      </div>
      <div class="pipeline-stage__body">
        <div class="row items-center q-gutter-sm">
          <span class="text-weight-medium">{{ t(`stages.${entry.stage}`) }}</span>
          <StatusPill
            :color="entry.color"
            :label="entry.record ? t(`stages.statuses.${entry.status}`) : t('stages.notStarted')" />
        </div>
        <div
          v-if="entry.record"
          class="text-caption text-grey-6 q-mt-xs">
          <template v-if="entry.record.overall_confidence !== null">
            {{ t('stages.confidence') }} {{ confidencePercent(entry.record.overall_confidence) }}%
          </template>
          <template v-if="entry.record.processing_time_ms">
            • {{ t('stages.duration') }} {{ formatDuration(entry.record.processing_time_ms) }}
          </template>
          <template v-if="entry.record.model_used"> • {{ entry.record.model_used }}</template>
        </div>
        <div
          v-if="entry.record?.error_message"
          class="text-caption text-negative q-mt-xs">
          {{ entry.record.error_message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  symRoundedCancel,
  symRoundedCheck,
  symRoundedHourglassEmpty,
  symRoundedMoreHoriz,
  symRoundedPriorityHigh,
  symRoundedSkipNext,
} from '@quasar/extras/material-symbols-rounded'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import StatusPill from 'src/CrudManager/Core/composites/Ui/StatusPill/StatusPill.vue'
import { computed } from 'vue'
import type { QueueStage } from '../types'

/**
 * The four pipeline stages as a vertical stepper — the order IS the
 * process (analysis → extraction → review → finalization). Shows the
 * latest execution of each stage with confidence, duration and errors.
 */
const CANONICAL_STAGES = ['analysis', 'extraction', 'review', 'finalize'] as const

const props = defineProps<{ stages: QueueStage[] | undefined }>()

const { t } = useCompositeI18n('core.AiDocumentProcessor')

interface TimelineEntry {
  stage: (typeof CANONICAL_STAGES)[number]
  record: QueueStage | null
  status: QueueStage['status'] | 'pending'
  color: string
  icon: string
}

const STAGE_STATUS_COLOR: Record<string, string> = {
  pending: 'muted',
  processing: 'info',
  completed: 'positive',
  failed: 'negative',
  skipped: 'muted',
  cancelled: 'muted',
}

const STAGE_STATUS_ICON: Record<string, string> = {
  pending: symRoundedHourglassEmpty,
  processing: symRoundedMoreHoriz,
  completed: symRoundedCheck,
  failed: symRoundedPriorityHigh,
  skipped: symRoundedSkipNext,
  cancelled: symRoundedCancel,
}

const timeline = computed<TimelineEntry[]>(() =>
  CANONICAL_STAGES.map((stage) => {
    const record =
      (props.stages ?? [])
        .filter((item) => item.stage === stage)
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0] ?? null
    const status = record?.status ?? 'pending'

    return {
      stage,
      record,
      status,
      color: STAGE_STATUS_COLOR[status] ?? 'muted',
      icon: STAGE_STATUS_ICON[status] ?? symRoundedHourglassEmpty,
    }
  }),
)

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

// Stages store confidence inconsistently: extraction as a 0-1 fraction, review as a 0-100 percentage
const confidencePercent = (value: number): number => Math.round(value <= 1 ? value * 100 : value)
</script>

<style lang="scss" scoped>
.pipeline-stage {
  display: flex;
  gap: 12px;

  &__rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 24px;
    flex-shrink: 0;
  }

  &__dot {
    --stage-color: #9e9e9e;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: color-mix(in srgb, var(--stage-color) 18%, transparent);
    color: var(--stage-color);
    border: 1px solid color-mix(in srgb, var(--stage-color) 40%, transparent);

    &--info { --stage-color: var(--q-info); }
    &--positive { --stage-color: var(--q-positive); }
    &--negative { --stage-color: var(--q-negative); }
    &--muted { --stage-color: #9e9e9e; }
  }

  &__line {
    flex: 1;
    width: 2px;
    min-height: 14px;
    margin: 2px 0;
    background: color-mix(in srgb, #9e9e9e 30%, transparent);

    &--done {
      background: color-mix(in srgb, var(--q-positive) 45%, transparent);
    }
  }

  &__body {
    padding-bottom: 14px;
    min-width: 0;
  }
}
</style>
